import { getBooleanSpecification, mergeAcmeConfiguration } from "common/helpers/catalogical-specifications";
import { PreCheckoutInformation } from "common/types";
import { CatalogicalItem, CatalogicalRequestContent, ItemStatus } from "common/types/catalogical-type";
import { useGetItem, useGetItems } from "hooks/catalogical-software";
import jp from "jsonpath";
import { IOrdersService, OrdersServiceFactory } from "lib/services";
import { ItemTaxonomyId, normalizeSoftwareTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import { IItemVersion, ItemDetails, ItemSubtypes, ItemTypes } from "lib/types/items";
import _ from "lodash";
import { useEffect, useState } from "react";

import jsonWithDefaultVersions from "./defaultVersions.json";
import { useGetItemDetailsResult } from "./types/itemDetails.types";

const GET_PARENT_INFO_IF_NULL = true;
const NO_CHILD_VERSION = undefined;

export const useCatalogicalGetSoftwareDetails = (): useGetItemDetailsResult => {
  const { item: itemParent, error: errorGetItem, isLoading: isLoadingGetItem, doGetItem } = useGetItem();
  const { items: itemChildren, error: errorGetItems, isLoading: isLoadingGetItems, doGetItems } = useGetItems();
  const [preCheckoutData, setPreCheckoutData] = useState<PreCheckoutInformation | undefined>(undefined);
  const [mappedItem, setMappedItem] = useState<ItemDetails | undefined>(undefined);
  const [defaultVersion, setDefaultVersion] = useState<IItemVersion | undefined>(undefined);
  const [versions, setVersions] = useState<IItemVersion[]>([]);

  const [error, setError] = useState<Error | undefined>(undefined);
  useEffect(() => {
    if (itemParent && itemChildren) {
      setHookInfo();
    }
  }, [itemParent, itemChildren]);

  const setHookInfo = () => {
    setVersions([]);
    setMappedItem(undefined);
    // Item has versions
    if (itemChildren && itemChildren.length > 0) {
      // set first item as default
      let defaultVersionToSelect: IItemVersion = mapVersion(itemChildren[0])!;
      let selectedChild: CatalogicalItem = itemChildren[0];

      // order by short description
      const orderedVersions = _.orderBy(itemChildren, [(item) => item.shortDescription?.toLowerCase()], ["asc"]);

      // check default items json for default version
      const itemVersionFromJson = jsonWithDefaultVersions.defaultVersions.find((entry) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        return entry.itemId === itemParent?.taxonomyId!;
      });
      // Find if there is a default version and move it to the first position
      if (orderedVersions && itemVersionFromJson) {
        const defaultVersionIndex = orderedVersions.findIndex(
          (version) => version.taxonomyId === itemVersionFromJson.defaultVersion.id
        );

        // Choose version for dropdown
        if (orderedVersions[defaultVersionIndex]) {
          defaultVersionToSelect = mapVersion(orderedVersions[defaultVersionIndex])!;
          selectedChild = orderedVersions[defaultVersionIndex];
        }

        if (defaultVersionIndex > 0) {
          const [defaultVersion] = orderedVersions.splice(defaultVersionIndex, 1);
          orderedVersions.unshift(defaultVersion);
        }
      }
      setMappedItem(mapItem(itemParent!, selectedChild, true));
      setDefaultVersion(defaultVersionToSelect);
      setVersions(mapVersions(orderedVersions));
    } else {
      setMappedItem(mapItem(itemParent!, NO_CHILD_VERSION));
      setDefaultVersion(mapVersion(itemParent));
    }
  };

  const fetchItem = async (taxonomyId: ItemTaxonomyId) => {
    doGetItem(taxonomyId.toString());
    const params: CatalogicalRequestContent = {
      limit: 500,
      offset: 0,
      sortBy: "name",
      sortDirection: "asc",
      filter: JSON.stringify({ parentTaxonomyId: taxonomyId.toString() }),
    };
    doGetItems({ searchParams: params });
  };

  const fetchPreCheckoutData = async (token: string) => {
    setPreCheckoutData(undefined);
    try {
      const isCurrentlySelectedItemTheParentItem = mappedItem?.taxonomyId === itemParent?.taxonomyId;
      const hasADefaultVersion = defaultVersion !== undefined;
      // Determine which version to order: if user is viewing parent item and a default version exists,
      // use the default version; otherwise use the currently selected/mapped item
      const versionToBeOrder =
        isCurrentlySelectedItemTheParentItem && hasADefaultVersion ? defaultVersion.taxonomyId : mappedItem?.taxonomyId;
      const taxonomyIdWithoutStrain = normalizeSoftwareTaxonomyId(ItemTaxonomyId.Parse(versionToBeOrder!).toString());
      const ordersService: IOrdersService = OrdersServiceFactory.getInstance(taxonomyIdWithoutStrain);
      const checkoutData = await ordersService.GetPreCheckoutInformation(taxonomyIdWithoutStrain, token);
      if (!checkoutData.information) {
        throw new Error("Couldn't obtain pre checkout information");
      }
      setPreCheckoutData(checkoutData.information);
      setError(undefined); // Clear any previous errors on successful fetch
    } catch (error) {
      setError(error as Error);
    }
  };

  const mapVersions = (versions: CatalogicalItem[]): IItemVersion[] => {
    if (!versions || versions.length === 0) {
      return [];
    }
    return versions.map((catalogVersion) => mapVersion(catalogVersion, GET_PARENT_INFO_IF_NULL)!);
  };

  const mapVersion = (version: CatalogicalItem | undefined, checkParent: boolean = false): IItemVersion | undefined => {
    if (!version) {
      return undefined;
    }

    const CAN_FALLBACK_TO_PARENT = true;

    // Extract ACME configuration from specifications with backward compatibility
    const acmeConfig = mergeAcmeConfiguration(version.specifications, version.acme);

    return {
      taxonomyId: version.taxonomyId,
      name: version.name,
      displayText: getValue(checkParent, version.shortDescription, "version.shortDescription"),
      status: version?.status || ItemStatus.unavailable,
      amount: getValue(checkParent, version.price?.amount, "price.amount", 0),
      currency: getValue(checkParent, version.price?.currency, "price.currency"),
      logoUrl: getValue(checkParent, version.logoUrl, "logoUrl"),
      orderWorkflow: getValue(checkParent, version.orderWorkflow, "orderWorkflow"),
      applinkInstanceName: getValue(checkParent, version?.applinkInstanceName, "applinkInstanceName"),
      applinkProductName: getValue(checkParent, version?.applinkProductName, "applinkProductName"),
      isReturnable: getBooleanSpecification(version.specifications, "isReturnable", false),
      orderRedirectUrl: getValue(checkParent, version?.references?.orderRedirectUrl, "references.orderRedirectUrl"),
      acme: acmeConfig,
      requiresLicenseCapValidation: getBooleanSpecification(
        version.specifications,
        "requiresLicenseCapValidation",
        false
      ),
      autoGenFormattedDescription: getValue(
        checkParent,
        version?.autoGenFormattedDescription,
        "autoGenFormattedDescription"
      ),
      productDownloadUrl: getValue(
        CAN_FALLBACK_TO_PARENT,
        version?.references?.productDownloadUrl,
        "references.productDownloadUrl"
      ),
    };
  };

  const updateVersionSelected = (selectedVersion: IItemVersion) => {
    if (itemChildren && itemChildren?.length > 0) {
      const newSelectedChild = itemChildren.find((child) => child.taxonomyId === selectedVersion.taxonomyId);
      setDefaultVersion(mapVersion(newSelectedChild, GET_PARENT_INFO_IF_NULL));

      const parentWithChildOverrides: CatalogicalItem | undefined = { ...itemParent! };

      // TODO: Refactor this logic, for now keeping as it was for V1
      // update applinkinstance and productname
      if (parentWithChildOverrides && newSelectedChild) {
        parentWithChildOverrides.applinkInstanceName = newSelectedChild.applinkInstanceName;
        parentWithChildOverrides.applinkProductName = newSelectedChild.applinkProductName;
        parentWithChildOverrides.isReturnable = newSelectedChild.isReturnable;
      }

      setMappedItem(mapItem(parentWithChildOverrides!, newSelectedChild!, GET_PARENT_INFO_IF_NULL));
    }
  };

  const mapItem = (
    item: CatalogicalItem,
    selectedVersion: Partial<CatalogicalItem> | undefined,
    checkParent: boolean = false
  ): ItemDetails => {
    // Extract ACME configuration from specifications with backward compatibility
    const acmeConfig = mergeAcmeConfiguration(item.specifications, item.acme);

    return {
      taxonomyId: getValue(checkParent, (selectedVersion ?? item).taxonomyId, "taxonomyId"),
      name: getValue(checkParent, item.name, "name"),
      description: getValue(checkParent, item.description, "description", ""),
      shortDescription: getValue(checkParent, item.shortDescription, "shortDescription", ""),
      status: (getValue(checkParent, item.status, "status") as string).toLowerCase(),
      logoUrl: getValue(checkParent, item.logoUrl, "logoUrl", ""),
      publisher: getValue(checkParent, item.manufacturer, "manufacturer", ""),
      etaDays: getValue(checkParent, item.etaDays, "etaDays", 0),
      os: item.os || [],
      categories: item.categories || item.tags || [],
      priceAmount: getValue(checkParent, (selectedVersion ?? item).price?.amount, "price.amount", 1),
      priceCurrency: getValue(checkParent, (selectedVersion ?? item).price?.currency, "price.currency", ""),
      hasStock: getValue(checkParent, item.hasStock, "hasStock", false),
      orderWorkflow: getValue(checkParent, item.orderWorkflow, "orderWorkflow"),
      orderRedirectUrl: getValue(
        checkParent,
        item.references?.orderRedirectUrl || item.orderRedirectUrl,
        "references.orderRedirectUrl"
      ),
      productDownloadUrl: getValue(checkParent, item.references?.productDownloadUrl, "references.productDownloadUrl"),
      externalButtonText: getValue(checkParent, item.externalButtonText, "externalButtonText"),
      moreDetailsUrl: getValue(checkParent, item.references?.moreDetailsUrl, "references.moreDetailsUrl"),
      parentName: itemParent?.name,
      parentTxId: itemParent?.taxonomyId || item.parentTaxonomyItemId,
      requiresLicenseCapValidation: getBooleanSpecification(item.specifications, "requiresLicenseCapValidation", false),
      isReturnable: getBooleanSpecification(item.specifications, "isReturnable", false),
      applinkProductName: item.applinkProductName,
      applinkInstanceName: item.applinkInstanceName,
      subtype: item.subtype as ItemSubtypes | undefined,
      acme: acmeConfig,
      itptClassification: item.itptClassification,
      version: item.version,
      dataClassification: item.dataClassification,
      securityStatus: item.securityStatus,
      feedback: item.feedback,
      autoGenFormattedDescription: item.autoGenFormattedDescription,
      complianceInformation: item.complianceInformation,
    };
  };

  /**
   * Gets a value from either the child or parent item, with fallback to a default value
   * @param checkParent - Whether to check the parent item if child value is not present
   * @param value - The value to check from the child item
   * @param key - The key path to look up in the parent item
   * @param defaultValue - Optional default value if no child or parent value exists
   * @returns The resolved value from child, parent, or default
   */
  const getValue = (checkParent: boolean, value: any, key: string, defaultValue?: any) => {
    const valueOrDefault = value ?? defaultValue ?? "";
    if (!checkParent) {
      return valueOrDefault;
    }

    // Should get value from parent if child value is not present
    if ((!value || value === "") && itemParent && jp.value(itemParent, key)) {
      return jp.value(itemParent, key);
    }

    return valueOrDefault;
  };

  return {
    type: ItemTypes.SOFTWARE,
    item: mappedItem,
    isLoading: isLoadingGetItem || isLoadingGetItems,
    error: errorGetItem ?? errorGetItems ?? error,
    defaultVersion,
    versions,
    updateVersionSelected,
    getItem: fetchItem,
    preCheckoutData,
    fetchPreCheckoutData,
  };
};
